"use client"

import type React from "react"

import { useState, useMemo, useEffect } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Pagination,
} from "@mui/material"
import { Visibility, Email, Phone, LocationOn, Work, School, Language, AttachMoney } from "@mui/icons-material"
import axios from "axios"
import { IonContent, IonPage } from "@ionic/react" 
import { baseUrl, config, requestID } from "../../../common/utils"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"

export const exportToPDF = (data: any[], title = "Applicants Report") => {
  const doc = new jsPDF("landscape")
  doc.setFontSize(18)
  doc.text(title, 14, 20)
  const rows = data.map((applicant, index) => [
    index + 1,
    applicant.full_name,
    applicant.email_address,
    applicant.job_functions,
    applicant.preferred_job_titles,
    `${applicant.total_years_experience} yrs`,
    `${applicant.location_city}, ${applicant.location_state_region}`,
    applicant.availability,
  ])
  autoTable(doc, {
    startY: 30,
    head: [["#", "Name", "Email", "Job Function", "Preferred Role", "Experience", "Location", "Availability"]],
    body: rows,
    styles: { fontSize: 8 },
  })
  doc.save("Applicants_Report.pdf")
}

export const exportToExcel = (data: any[], filename: string) => {
  const worksheet = XLSX.utils.json_to_sheet(data)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, "Applicants")
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  })
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  })
  saveAs(blob, `${filename}.xlsx`)
}

const ApplicantManagement:React.FC=() =>{
  const [applicants, setApplicants] = useState([])
  const [loading, setLoading] = useState(true)
  const [projects, setProjects] = useState([])
  const [selectedApplicant, setSelectedApplicant] = useState<any>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)
  const [selectedProject, setSelectedProject] = useState("")
  const [rowsPerPage, setRowsPerPage] = useState(10)

  useEffect(() => {
    fetchApplicants()
    fetchProjects()
  }, [])

  const fetchApplicants = async () => {
    try {
      setLoading(true)
      const payload = { requestID: requestID() }
      const res = await axios.post(baseUrl() + "atss/getAllapplicants", payload, config)
      console.log("All applicants:", res)
      setApplicants(res.data.data || [])
    } catch (error) {
      console.error("Error fetching applicants:", error)
      setApplicants([])
    } finally {
      setLoading(false)
    }
  }

  const fetchProjects = async () => {
    try {
      setLoading(true)
      const payload = { requestID: requestID() }
      const res = await axios.post(baseUrl() + "projects/getAllProjects", payload, config)
      console.log(res)
      setProjects(res.data.data || [])
    } catch (error) {
      console.error("Error fetching Project:", error)
      setProjects([])
    } finally {
      setLoading(false)
    }
  }

  const fetchApplicantsByProject = async (projectId: string) => {
    try {
      setLoading(true)
      const payload = {
        requestID: requestID(),
        project_id: projectId,
      }
      const res = await axios.post(baseUrl() + "projects/getApplicantByProject", payload, config)
      console.log(res)
      console.log("Filtered applicants:", res)
      setApplicants(res.data.data || [])
    } catch (error) {
      console.error("Error fetching applicants by project:", error)
      setApplicants([])
    } finally {
      setLoading(false)
    }
  }

  const handleExport = () => {
    const exportData = filteredApplicants.map((applicant: any) => ({
      Name: applicant.full_name,
      Email: applicant.email_address,
      JobFunction: applicant.job_functions,
      PreferredRole: applicant.preferred_job_titles,
      Experience: `${applicant.total_years_experience} years`,
      Location: `${applicant.location_city}, ${applicant.location_state_region}`,
      Availability: applicant.availability,
    }))
    exportToExcel(exportData, "Filtered_Applicants")
  }

  const filteredApplicants = useMemo(() => {
    const filtered =
      applicants?.filter((applicant: any) => {
        const matchesSearch =
          applicant?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          applicant?.email_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          applicant?.preferred_job_titles?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          applicant?.job_functions?.toLowerCase().includes(searchTerm.toLowerCase())
        return matchesSearch
      }) || []
    return filtered
  }, [applicants, searchTerm])

  const paginatedApplicants = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return filteredApplicants.slice(startIndex, endIndex)
  }, [filteredApplicants, page, rowsPerPage])

  const totalPages = Math.ceil(filteredApplicants.length / rowsPerPage)

  const handleViewDetails = (applicant: any) => {
    setSelectedApplicant(applicant)
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedApplicant(null)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage)
  }

  const handleExportPDF = () => {
    exportToPDF(filteredApplicants, "Filtered Applicants Report")
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(1)
  }

  useEffect(() => {
    setPage(1)
  }, [searchTerm])

  return (
    <IonPage className="pushDown">
      <IonContent>
        <Box sx={{ p: 3, backgroundColor: (theme) => theme.palette.background.default, minHeight: "100vh" }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 3, color: "text.primary" }}>
            Applicant Management
          </Typography>

          {/* Filters */}
          <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
            <TextField
              label="Search applicants"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 250 }}
              placeholder="Search by name, email, job title, or function"
            />
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel id="project-select-label">Select Project</InputLabel>
              <Select
                labelId="project-select-label"
                label="Select Project"
                value={selectedProject}
                onChange={(e) => {
                  const projectId = e.target.value as string
                  setSelectedProject(projectId)
                  if (projectId) {
                    fetchApplicantsByProject(projectId)
                  } else {
                    fetchApplicants()
                  }
                }}
                variant="outlined"
              >
                <MenuItem value="">All Projects</MenuItem>
                {projects.map((project: any) => (
                  <MenuItem key={project.id} value={project.project_id}>
                    {project.project_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button variant="outlined" onClick={fetchApplicants} disabled={loading} sx={{ minWidth: 120 }}>
              {loading ? "Loading..." : "Refresh"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExport}
              disabled={filteredApplicants.length === 0}
            >
              Export to Excel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExportPDF}
              disabled={filteredApplicants.length === 0}
            >
              Export to PDF
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <Typography color="text.secondary">Loading applicants...</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 3 }}>
              <Table>
                <TableHead sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Job Function</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Preferred Role</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Experience</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Availability</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedApplicants?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="text.secondary">No applicants found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedApplicants?.map((applicant: any) => (
                      <TableRow key={applicant.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
                            {applicant.full_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{applicant.email_address}</TableCell>
                        <TableCell>
                          <Chip label={applicant.job_functions || "N/A"} size="small" color="primary" />
                        </TableCell>
                        <TableCell>{applicant.preferred_job_titles}</TableCell>
                        <TableCell>{applicant.total_years_experience} years</TableCell>
                        <TableCell>
                          {applicant.location_city}, {applicant.location_state_region}
                        </TableCell>
                        <TableCell>
                          <Chip label={applicant.availability} size="small" color="success" />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton onClick={() => handleViewDetails(applicant)} color="primary">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination Controls */}
          {!loading && filteredApplicants.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min((page - 1) * rowsPerPage + 1, filteredApplicants.length)} to{" "}
                  {Math.min(page * rowsPerPage, filteredApplicants.length)} of {filteredApplicants.length} applicants
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Rows per page</InputLabel>
                  <Select value={rowsPerPage} label="Rows per page" onChange={handleRowsPerPageChange}>
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Applicant Details Modal */}
          <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
            <DialogTitle>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Applicant Details
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {selectedApplicant && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={3}>
                    {/* Personal Information */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ boxShadow: 1 }}>
                        <CardContent>
                          <Typography
                            variant="h6"
                            gutterBottom
                            color="primary"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <Visibility sx={{ mr: 1 }} /> Personal Information
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold", mr: 1 }}>
                                  Full Name:
                                </Typography>
                                <Typography variant="body2">{selectedApplicant.full_name}</Typography>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Email sx={{ mr: 1, fontSize: 16, color: "action.active" }} />
                                <Typography variant="body2">{selectedApplicant.email_address}</Typography>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Phone sx={{ mr: 1, fontSize: 16, color: "action.active" }} />
                                <Typography variant="body2">{selectedApplicant.phone_number}</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold", mr: 1 }}>
                                  Date of Birth:
                                </Typography>
                                <Typography variant="body2">{formatDate(selectedApplicant.date_of_birth)}</Typography>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold", mr: 1 }}>
                                  Gender:
                                </Typography>
                                <Typography variant="body2">{selectedApplicant.gender}</Typography>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold", mr: 1 }}>
                                  Nationality:
                                </Typography>
                                <Typography variant="body2">{selectedApplicant.nationality}</Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                    {/* Location Information */}
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined" sx={{ boxShadow: 1 }}>
                        <CardContent>
                          <Typography
                            variant="h6"
                            gutterBottom
                            color="primary"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <LocationOn sx={{ mr: 1 }} /> Location
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Country:</strong> {selectedApplicant.location_country}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>State:</strong> {selectedApplicant.location_state_region}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>City:</strong> {selectedApplicant.location_city}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Area:</strong> {selectedApplicant.location_area}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    {/* Education */}
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined" sx={{ boxShadow: 1 }}>
                        <CardContent>
                          <Typography
                            variant="h6"
                            gutterBottom
                            color="primary"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <School sx={{ mr: 1 }} /> Education
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Level:</strong> {selectedApplicant.highest_education_level}
                          </Typography>
                          {selectedApplicant.field_of_study && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Field:</strong> {selectedApplicant.field_of_study}
                            </Typography>
                          )}
                          {selectedApplicant.institution_name && (
                            <Typography variant="body2">
                              <strong>Institution:</strong> {selectedApplicant.institution_name}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    {/* Work Preferences */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ boxShadow: 1 }}>
                        <CardContent>
                          <Typography
                            variant="h6"
                            gutterBottom
                            color="primary"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <Work sx={{ mr: 1 }} /> Work Preferences & Experience
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Job Function:</strong> {selectedApplicant.job_functions}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Preferred Role:</strong> {selectedApplicant.preferred_job_titles}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Preferred Industry:</strong> {selectedApplicant.preferred_industries}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Employment Type:</strong> {selectedApplicant.employment_type_preferences}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Total Experience:</strong> {selectedApplicant.total_years_experience} years
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Specific Experience:</strong> {selectedApplicant.specific_roles_experience}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Remote Work:</strong> {selectedApplicant.remote_work_preference ? "Yes" : "No"}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Willing to Relocate:</strong>{" "}
                                {selectedApplicant.willing_to_relocate ? "Yes" : "No"}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                    {/* Skills & Languages */}
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined" sx={{ boxShadow: 1 }}>
                        <CardContent>
                          <Typography
                            variant="h6"
                            gutterBottom
                            color="primary"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <Language sx={{ mr: 1 }} /> Skills & Languages
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Languages:</strong> {selectedApplicant.languages_spoken}
                          </Typography>
                          {selectedApplicant.skills && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Skills:</strong> {selectedApplicant.skills}
                            </Typography>
                          )}
                          {selectedApplicant.certifications && (
                            <Typography variant="body2">
                              <strong>Certifications:</strong> {selectedApplicant.certifications}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    {/* Salary & Availability */}
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined" sx={{ boxShadow: 1 }}>
                        <CardContent>
                          <Typography
                            variant="h6"
                            gutterBottom
                            color="primary"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <AttachMoney sx={{ mr: 1 }} /> Salary & Availability
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Salary Range:</strong> {formatCurrency(selectedApplicant.salary_expectation_min)} -{" "}
                            {formatCurrency(selectedApplicant.salary_expectation_max)}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Availability:</strong> {selectedApplicant.availability}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Work Authorization:</strong> {selectedApplicant.work_authorization_status}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal} color="primary" variant="contained">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>
    </IonPage>
  )
}
export default  ApplicantManagement
